import { axiosWithAuth } from "@/services/legacy/api/axiosInstances";
import { errorCatch } from "@/services/legacy/api/error";
import { createLogger } from "@/utils/logger";
import { PropertyData } from "@/components/ui/PropertyCard/ContextMenu";

const logger = createLogger("properties-actions-service");

export interface PropertyActionError {
  statusCode: number;
  message: string;
  details?: any;
}

export class PropertyActionServiceError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: any,
  ) {
    super(message);
    this.name = "PropertyActionServiceError";
  }
}

export interface ActualizePropertyResponse {
  message: string;
}

export interface DuplicatePropertyResponse {
  message: string;
  newPropertyId: string;
  slug: string;
}

export interface ArchivePropertyRequest {
  propertyId: string;
  realtorFields: {
    publicationStatus: "archived";
    archiveComment: string;
  };
}

export interface ChangeStatusRequest {
  propertyId: string;
  realtorFields: {
    publicationStatus: "draft" | "published" | "pending" | "archived";
  };
}

export interface AssignAgentRequest {
  propertyId: string;
  realtorFields: {
    responsibleAgent: string | null;
  };
}

export const propertiesActionsService = {
  /**
   * Actualize property (refresh/bump publication date)
   */
  async actualizeProperty(
    propertyId: string,
    options?: { updatePublicationDate?: boolean; notifyOwner?: boolean },
  ): Promise<ActualizePropertyResponse> {
    const url = `/api/property/actualize-property`;

    try {
      logger.info(`[actualizeProperty] Actualizing property: ${propertyId}`);

      const response = await axiosWithAuth.patch<ActualizePropertyResponse>(
        url,
        {
          propertyId,
          options,
        },
      );

      logger.info(
        `[actualizeProperty] Successfully actualized property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        options,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[actualizeProperty] Failed to actualize property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "actualize"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Duplicate property
   */
  async duplicateProperty(
    originalPropertyId: string,
    options?: {
      copyImages?: boolean;
      copyLayouts?: boolean;
      publishStatus?: "draft" | "published" | "pending";
    },
  ): Promise<DuplicatePropertyResponse> {
    const url = `/api/property/duplicate-property`;

    try {
      logger.info(
        `[duplicateProperty] Duplicating property: ${originalPropertyId}`,
      );

      const response = await axiosWithAuth.post<DuplicatePropertyResponse>(
        url,
        {
          originalPropertyId,
          options,
        },
      );

      logger.info(
        `[duplicateProperty] Successfully duplicated property: ${originalPropertyId} -> ${response.data.newPropertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        originalPropertyId,
        options,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[duplicateProperty] Failed to duplicate property ${originalPropertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "duplicate"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Archive property with comment
   */
  async archiveProperty(
    propertyId: string,
    comment: string,
  ): Promise<{ message: string }> {
    const url = `/properties/${propertyId}/status`;

    try {
      logger.info(`[archiveProperty] Archiving property: ${propertyId}`);

      const body: { status: string; comment?: string } = {
        status: "archived",
      };

      if (comment) {
        body.comment = comment;
      }

      const response = await axiosWithAuth.put<{ message: string }>(
        url,
        body,
      );

      logger.info(
        `[archiveProperty] Successfully archived property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        comment,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[archiveProperty] Failed to archive property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "archive"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Restore archived property
   */
  async restoreProperty(
    propertyId: string,
    comment?: string,
  ): Promise<{ message: string }> {
    const url = `/properties/${propertyId}/status`;

    try {
      logger.info(
        `[restoreProperty] Restoring archived property: ${propertyId}`,
      );

      const body: { status: string; comment?: string } = {
        status: "under_moderation",
      };

      const response = await axiosWithAuth.put<{ message: string }>(
        url,
        body,
      );

      logger.info(
        `[restoreProperty] Successfully restored property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        comment,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[restoreProperty] Failed to restore property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "restore"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Change publication status
   */
  async changePublicationStatus(
    propertyId: string,
    status: "draft" | "published" | "pending" | "archived",
  ): Promise<{ message: string }> {
    const url = `/property/change-property`;

    try {
      logger.info(
        `[changePublicationStatus] Changing status for property ${propertyId} to: ${status}`,
      );

      // Create FormData to match addObject.service.ts pattern
      const formData = new FormData();
      formData.append("propertyId", propertyId);
      formData.append(
        "realtorFields",
        JSON.stringify({
          publicationStatus: status,
        }),
      );

      const response = await axiosWithAuth.patch<{ message: string }>(
        url,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      logger.info(
        `[changePublicationStatus] Successfully changed status for property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        status,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[changePublicationStatus] Failed to change status for property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "changeStatus"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Assign or unassign agent
   */
  async assignAgent(
    propertyId: string,
    agentId: string | null,
  ): Promise<{ message: string }> {
    const url = `/property/change-property`;

    try {
      logger.info(
        `[assignAgent] ${agentId ? "Assigning" : "Unassigning"} agent for property: ${propertyId}`,
      );

      // Create FormData to match addObject.service.ts pattern
      const formData = new FormData();
      formData.append("propertyId", propertyId);
      formData.append(
        "realtorFields",
        JSON.stringify({
          responsibleAgent: agentId,
        }),
      );

      const response = await axiosWithAuth.patch<{ message: string }>(
        url,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      logger.info(
        `[assignAgent] Successfully ${agentId ? "assigned" : "unassigned"} agent for property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        agentId,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[assignAgent] Failed to assign agent for property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "assignAgent"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Change exclusive type
   */
  async changeExclusiveType(
    propertyId: string,
    exclusiveType: "my" | "agency" | "other_agency" | "owner",
  ): Promise<{ message: string }> {
    const url = `/property/change-property`;

    try {
      logger.info(
        `[changeExclusiveType] Changing exclusive type for property ${propertyId} to: ${exclusiveType}`,
      );

      // Create FormData to match addObject.service.ts pattern
      const formData = new FormData();
      formData.append("propertyId", propertyId);
      formData.append(
        "realtorFields",
        JSON.stringify({
          exclusiveType,
        }),
      );

      const response = await axiosWithAuth.patch<{ message: string }>(
        url,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      logger.info(
        `[changeExclusiveType] Successfully changed exclusive type for property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        exclusiveType,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[changeExclusiveType] Failed to change exclusive type for property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "changeExclusive"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Update call status
   */
  async updateCallStatus(
    propertyId: string,
    callStatus:
      | "not_called"
      | "no_answer"
      | "interested"
      | "not_interested"
      | "wrong_number",
  ): Promise<{ message: string }> {
    const url = `/property/change-property`;

    try {
      logger.info(
        `[updateCallStatus] Updating call status for property ${propertyId} to: ${callStatus}`,
      );

      // Create FormData to match addObject.service.ts pattern
      const formData = new FormData();
      formData.append("propertyId", propertyId);
      formData.append(
        "realtorFields",
        JSON.stringify({
          callStatus,
          callDate: new Date().toISOString(),
        }),
      );

      const response = await axiosWithAuth.patch<{ message: string }>(
        url,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      logger.info(
        `[updateCallStatus] Successfully updated call status for property: ${propertyId}`,
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = errorCatch(error);
      const statusCode = error?.response?.status || 500;

      const errorDetails = {
        propertyId,
        callStatus,
        statusCode,
        originalErrorSource: "axios",
        errorMessage,
        responseData: error?.response?.data,
      };

      logger.error(
        `[updateCallStatus] Failed to update call status for property ${propertyId}. Status: ${statusCode}, Error: ${errorMessage}`,
        errorDetails,
      );

      throw new PropertyActionServiceError(
        this.getErrorMessage(statusCode, "updateCall"),
        statusCode,
        errorDetails,
      );
    }
  },

  /**
   * Batch update properties (for bulk operations)
   */
  async batchUpdateProperties(
    updates: Array<{
      propertyId: string;
      realtorFields: Partial<PropertyData>;
    }>,
  ): Promise<{ message: string; updated: number }> {
    try {
      logger.info(
        `[batchUpdateProperties] Updating ${updates.length} properties`,
      );

      // Execute all updates in parallel with FormData
      const promises = updates.map((update) => {
        const formData = new FormData();
        formData.append("propertyId", update.propertyId);
        formData.append("realtorFields", JSON.stringify(update.realtorFields));

        return axiosWithAuth.patch(`/property/change-property`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      });

      const results = await Promise.allSettled(promises);
      const successful = results.filter(
        (result) => result.status === "fulfilled",
      ).length;
      const failed = results.length - successful;

      if (failed > 0) {
        logger.warn(
          `[batchUpdateProperties] ${failed} out of ${results.length} updates failed`,
        );
      }

      logger.info(
        `[batchUpdateProperties] Successfully updated ${successful} properties`,
      );

      return {
        message: `Updated ${successful} properties${failed > 0 ? `, ${failed} failed` : ""}`,
        updated: successful,
      };
    } catch (error: any) {
      const errorMessage = errorCatch(error);

      logger.error(
        `[batchUpdateProperties] Batch update failed: ${errorMessage}`,
        { updates, error },
      );

      throw new PropertyActionServiceError("Batch update failed", 500, {
        updates,
        error,
      });
    }
  },

  /**
   * Get user-friendly error messages based on status code and action
   */
  getErrorMessage(statusCode: number, action: string): string {
    const actionMessages: Record<string, Record<number, string>> = {
      actualize: {
        401: "Authentication required",
        403: "Insufficient permissions to actualize property",
        404: "Property not found",
        409: "Property cannot be actualized at this time",
        429: "Too many requests, please try again later",
        500: "Failed to actualize property",
      },
      duplicate: {
        401: "Authentication required",
        403: "Insufficient permissions to duplicate property",
        404: "Property not found",
        409: "Property cannot be duplicated",
        429: "Too many requests, please try again later",
        500: "Failed to duplicate property",
      },
      archive: {
        401: "Authentication required",
        403: "Insufficient permissions to archive property",
        404: "Property not found",
        409: "Property is already archived",
        429: "Too many requests, please try again later",
        500: "Failed to archive property",
      },
      changeStatus: {
        401: "Authentication required",
        403: "Insufficient permissions to change status",
        404: "Property not found",
        409: "Invalid status change",
        429: "Too many requests, please try again later",
        500: "Failed to change status",
      },
      assignAgent: {
        401: "Authentication required",
        403: "Insufficient permissions to assign agent",
        404: "Property or agent not found",
        409: "Agent assignment conflict",
        429: "Too many requests, please try again later",
        500: "Failed to assign agent",
      },
      restore: {
        401: "Authentication required",
        403: "Insufficient permissions to restore property",
        404: "Property not found",
        409: "Property is not archived or cannot be restored",
        429: "Too many requests, please try again later",
        500: "Failed to restore property",
      },
    };

    return (
      actionMessages[action]?.[statusCode] || "An unexpected error occurred"
    );
  },

  /**
   * Validate property data before sending requests
   */
  validatePropertyData(propertyData: Partial<PropertyData>): boolean {
    if (!propertyData.id) {
      throw new Error("Property ID is required");
    }

    if (
      propertyData.publicationStatus &&
      !["draft", "published", "pending", "archived"].includes(
        propertyData.publicationStatus,
      )
    ) {
      throw new Error("Invalid publication status");
    }

    if (
      propertyData.exclusiveType &&
      !["my", "agency", "other_agency", "owner"].includes(
        propertyData.exclusiveType,
      )
    ) {
      throw new Error("Invalid exclusive type");
    }

    if (
      propertyData.callStatus &&
      ![
        "not_called",
        "no_answer",
        "interested",
        "not_interested",
        "wrong_number",
      ].includes(propertyData.callStatus)
    ) {
      throw new Error("Invalid call status");
    }

    return true;
  },

  /**
   * Estimate operation time for UI feedback
   */
  getEstimatedOperationTime(action: string): number {
    const operationTimes: Record<string, number> = {
      actualize: 1000, // 1 second
      duplicate: 3000, // 3 seconds
      archive: 1500, // 1.5 seconds
      changeStatus: 800, // 0.8 seconds
      assignAgent: 1000, // 1 second
    };

    return operationTimes[action] || 1000;
  },
};
